<template>
  <div class="product-preview">
    <div class="d-flex flex-row-reverse mb-3" v-if="mode === 'edit'">
      <b-link
        class="export-csv my-md-0 my-2"
        v-if="exportData"
        :disabled="totalRecords == '00'"
        @click="download"
        >{{ $t(`general.export-csv`) }}
        <i class="icon-download ml-1"></i>
      </b-link>
    </div>
    <b-table
      hover
      responsive
      :items="items"
      :fields="fields"
      :per-page="perPage"
      :current-page="currentPage"
      show-empty
      :empty-text="$t('general.no-data-msg')"
      :tbody-tr-class="rowClass"
    >
      <template #head(products)>
        {{ $t(`${moduleSlug}.table_head_products`) }}
      </template>
      <template #head(pricingManagerId)>
        {{ $t(`${moduleSlug}.table_head_pricingManagerId`) }}
      </template>
      <template #head(productTagManagerId)>
        {{ $t(`${moduleSlug}.table_head_tagManagerId`) }}
      </template>
      <template #head(siteName)>
        {{ $t(`${moduleSlug}.table_head_siteName`) }}
      </template>
      <template #head(sku)>
        {{ $t(`${moduleSlug}.table_head_sku`) }}
      </template>
      <template #head(vmn)>
        {{ $t(`${moduleSlug}.table_head_vmn`) }}
      </template>
      <template #head(everydayCost)>
        {{ $t(`${moduleSlug}.table_head_everydayCost`) }}
      </template>
      <template #head(regularPrice)>
        {{ $t(`${moduleSlug}.table_head_regularPrice`) }}
      </template>
      <template #head(promoPrice)>
        {{ $t(`${moduleSlug}.table_head_promoPrice`) }}
      </template>
      <template #head(replacementCost)="data">
        {{ data.label }}
      </template>
      <template #head(flyerImportCost)="data">
        {{ data.label }}
      </template>
      <template #head(flyerImportPrice)>
        {{ $t(`${moduleSlug}.table_head_flyerImportPrice`) }}
      </template>
      <template #head(promotionalImportCost)="data">
        {{ data.label }}
      </template>
      <template #head(promotionalImportPrice)>
        {{ $t(`${moduleSlug}.table_head_promotionalImportPrice`) }}
      </template>
      <template #head(flatFlyerRegularPrice)>
        {{ $t(`${moduleSlug}.table_head_flatFlyerRegularPrice`) }}
      </template>
      <template #head(flatFlyerPrice)>
        {{ $t(`${moduleSlug}.table_head_flatFlyerPrice`) }}
      </template>

      <template #head(everydayImportCost)>
        {{ $t(`${moduleSlug}.table_head_everydayCost`) }}
      </template>
      <template #head(cost)="data">
        {{
          formName === "everyday-pricing-import"
            ? $t(`${moduleSlug}.table_head_everydayCost`)
            : data.label
        }}
      </template>
      <template #head(everydayImportPrice)>
        {{ $t(`${moduleSlug}.table_head_regularPrice`) }}
      </template>

      <template #cell(products)="data">
        <div class="filtered-products-content">
          <template v-if="shouldHideImportProductName(data.item)">
            <span class="product-dash-icon-slot">-</span>
            <span class="product-dash-name-slot">-</span>
          </template>
          <template v-else>
            <b-img
              src="/media/icons/rectangle.svg"
              alt="Product Rectangle Icon"
              class="product-icon"
              v-if="showProductIcon"
            />
            <span class="product-name-text">{{ data.item.productName }}</span>
          </template>
        </div>
      </template>

      <template #cell(productTagManagerId)="data">
        {{ data.item.productTagManagerId }}
      </template>

      <template #cell(pricingManagerId)="data">
        {{ data.item.pricingManagerId }}
      </template>

      <template #cell(siteName)="data">
        <div class="d-flex flex-wrap align-items-center stores-cell-badges">
          <span
            v-for="(store, i) in (getSiteNames(data.item).length > 8 ? getSiteNames(data.item).slice(0, 7) : getSiteNames(data.item))"
            :key="i"
            class="badge-orange-badge"
          >
            {{ store }}
          </span>
          <span
            v-if="getSiteNames(data.item).length > 8"
            class="badge-orange-badge"
            v-b-tooltip.hover="getSiteNames(data.item).slice(7).join(', ')"
          >
            +{{ getSiteNames(data.item).length - 7 }}
          </span>
        </div>
      </template>

      <template #cell(sku)="data">
        {{ data.item.productId }}
      </template>

      <template #cell(vmn)="data">
        <span v-if="shouldHideImportVmn(data.item)" class="import-cell-placeholder">-</span>
        <span v-else>{{
          data.item.vendorModelNumber ||
            data.item.vmn ||
            data.item.VendorModelNumber
        }}</span>
      </template>

      <template #cell(everydayCost)="data">
        $&nbsp;{{ data.item.replacementCost }}
      </template>

      <template #cell(cost)="data">
        <span v-if="shouldHideImportCost(data.item)" class="import-cell-placeholder">-</span>
        <div
          v-else-if="formName === 'everyday-pricing-import'"
          class="price-edit-container"
        >
          <div class="price-text read-only">
            $&nbsp;{{ getEverydayImportCost(data.item) }}
          </div>
        </div>
        <div
          v-else-if="
            formName === 'flyer-pricing-import' ||
            formName === 'promotional-pricing-import'
          "
          class="price-edit-container"
        >
          <div class="price-text read-only">
            $&nbsp;{{ getImportCost(data.item) }}
          </div>
        </div>
      </template>

      <template #cell(regularPrice)="data">
        $&nbsp;{{ data.item.regularPrice }}
      </template>

      <template #cell(promoPrice)="data">
        $&nbsp;{{ data.item.promoPrice }}
      </template>

      <template #cell(replacementCost)="data">
        $&nbsp;{{ data.item.replacementCost }}
      </template>

      <template #cell(flyerImportCost)="data">
        <span v-if="shouldHideImportAmount(data.item)" class="import-cell-placeholder">-</span>
        <div class="price-edit-container" v-else-if="formName === 'flyer-pricing-import'">
          <div
            v-if="
              !canEditFlyerPromoImportPrice(data.item) ||
              editingIndex !== data.index + '_cost' ||
              data.item.statusDisplay !== 'valid'
            "
            @click="
              canEditFlyerPromoImportPrice(data.item) &&
                (editingIndex = data.index + '_cost')
            "
            :class="{
              'price-text': true,
              clickable: canEditFlyerPromoImportPrice(data.item),
            }"
          >
            $&nbsp;{{ data.item.compareAtPrice }}
          </div>
          <b-form-input
            v-else
            v-model="data.item.compareAtPrice"
            type="number"
            min="1"
            step="1"
            class="price-input"
            @input="
              (e) => {
                const v = Number(e?.target?.value);
                if (!Number.isNaN(v) && v <= 0) data.item.compareAtPrice = 1;
              }
            "
            @blur="
              () => {
                const v = Number(data.item.compareAtPrice);
                if (!Number.isNaN(v) && v <= 0) data.item.compareAtPrice = 1;
                editingIndex = null;
              }
            "
            @keyup.enter="editingIndex = null"
            autofocus
          ></b-form-input>
        </div>
        <template v-else>
          $&nbsp;{{ data.item.compareAtPrice }}
        </template>
      </template>

      <template #cell(flyerImportPrice)="data">
        <span v-if="shouldHideImportAmount(data.item)" class="import-cell-placeholder">-</span>
        <div class="price-edit-container" v-else-if="formName === 'flyer-pricing-import'">
          <div
            v-if="
              !canEditFlyerPromoImportPrice(data.item) ||
              editingIndex !== data.index + '_price' ||
              data.item.statusDisplay !== 'valid'
            "
            @click="
              canEditFlyerPromoImportPrice(data.item) &&
                (editingIndex = data.index + '_price')
            "
            :class="{
              'price-text': true,
              clickable: canEditFlyerPromoImportPrice(data.item),
            }"
          >
            $&nbsp;{{ data.item.promoPrice }}
          </div>
          <b-form-input
            v-else
            v-model="data.item.promoPrice"
            type="number"
            min="1"
            step="1"
            class="price-input"
            @input="
              (e) => {
                const v = Number(e?.target?.value);
                if (!Number.isNaN(v) && v <= 0) data.item.promoPrice = 1;
              }
            "
            @blur="
              () => {
                const v = Number(data.item.promoPrice);
                if (!Number.isNaN(v) && v <= 0) data.item.promoPrice = 1;
                editingIndex = null;
              }
            "
            @keyup.enter="editingIndex = null"
            autofocus
          ></b-form-input>
        </div>
        <template v-else>
          $&nbsp;{{ data.item.promoPrice }}
        </template>
      </template>

      <template #cell(promotionalImportCost)="data">
        <span v-if="shouldHideImportAmount(data.item)" class="import-cell-placeholder">-</span>
        <div class="price-edit-container" v-else-if="formName === 'promotional-pricing-import'">
          <div
            v-if="
              !canEditFlyerPromoImportPrice(data.item) ||
              editingIndex !== data.index + '_cost' ||
              data.item.statusDisplay !== 'valid'
            "
            @click="
              canEditFlyerPromoImportPrice(data.item) &&
                (editingIndex = data.index + '_cost')
            "
            :class="{
              'price-text': true,
              clickable: canEditFlyerPromoImportPrice(data.item),
            }"
          >
            $&nbsp;{{ data.item.compareAtPrice }}
          </div>
          <b-form-input
            v-else
            v-model="data.item.compareAtPrice"
            type="number"
            min="1"
            step="1"
            class="price-input"
            @input="
              (e) => {
                const v = Number(e?.target?.value);
                if (!Number.isNaN(v) && v <= 0) data.item.compareAtPrice = 1;
              }
            "
            @blur="
              () => {
                const v = Number(data.item.compareAtPrice);
                if (!Number.isNaN(v) && v <= 0) data.item.compareAtPrice = 1;
                editingIndex = null;
              }
            "
            @keyup.enter="editingIndex = null"
            autofocus
          ></b-form-input>
        </div>
        <template v-else>
          $&nbsp;{{ data.item.compareAtPrice }}
        </template>
      </template>
      <template #cell(promotionalImportPrice)="data">
        <span v-if="shouldHideImportAmount(data.item)" class="import-cell-placeholder">-</span>
        <div class="price-edit-container" v-else-if="formName === 'promotional-pricing-import'">
          <div
            v-if="
              !canEditFlyerPromoImportPrice(data.item) ||
              editingIndex !== data.index + '_price' ||
              data.item.statusDisplay !== 'valid'
            "
            @click="
              canEditFlyerPromoImportPrice(data.item) &&
                (editingIndex = data.index + '_price')
            "
            :class="{
              'price-text': true,
              clickable: canEditFlyerPromoImportPrice(data.item),
            }"
          >
            $&nbsp;{{ data.item.promoPrice }}
          </div>
          <b-form-input
            v-else
            v-model="data.item.promoPrice"
            type="number"
            min="1"
            step="1"
            class="price-input"
            @input="
              (e) => {
                const v = Number(e?.target?.value);
                if (!Number.isNaN(v) && v <= 0) data.item.promoPrice = 1;
              }
            "
            @blur="
              () => {
                const v = Number(data.item.promoPrice);
                if (!Number.isNaN(v) && v <= 0) data.item.promoPrice = 1;
                editingIndex = null;
              }
            "
            @keyup.enter="editingIndex = null"
            autofocus
          ></b-form-input>
        </div>
        <template v-else>
          $&nbsp;{{ data.item.promoPrice }}
        </template>
      </template>

      <template #cell(flatFlyerRegularPrice)="data">
        $&nbsp;{{ data.item.regularPrice }}
      </template>

      <template #cell(flatFlyerPrice)="data">
        $&nbsp;{{ data.item.flyerPrice }}
      </template>

      <template #cell(flyerImportStatus)="data">
        <i class="icon-success" v-if="data.item.statusDisplay === 'valid'" v-b-tooltip.hover :title="data.item.statusMessage"></i>
        <i class="icon-warning text-warning" v-else-if="data.item.statusDisplay === 'warning'" v-b-tooltip.hover :title="data.item.statusMessage"></i>
        <i class="icon-error text-danger" v-else v-b-tooltip.hover :title="data.item.statusMessage"></i>
      </template>

      <template #cell(everydayImportCost)="data">
        <span v-if="shouldHideImportCost(data.item)" class="import-cell-placeholder">-</span>
        <div
          v-else-if="formName === 'everyday-pricing-import'"
          class="price-edit-container"
        >
          <div class="price-text read-only">
            $&nbsp;{{ getEverydayImportCost(data.item) }}
          </div>
        </div>
        <template v-else>
          $&nbsp;{{ data.item.replacementCost }}
        </template>
      </template>
      <template #cell(everydayImportPrice)="data">
        <span v-if="shouldHideImportAmount(data.item)" class="import-cell-placeholder">-</span>
        <div class="price-edit-container" v-else-if="formName === 'everyday-pricing-import'">
          <div
            v-if="editingIndex !== data.index + '_price' || data.item.statusDisplay !== 'valid'"
            @click="data.item.statusDisplay === 'valid' && (editingIndex = data.index + '_price')"
            :class="{ 'price-text': true, clickable: data.item.statusDisplay === 'valid' }"
          >
            $&nbsp;{{ data.item.compareAtPrice }}
          </div>
          <b-form-input
            v-else
            v-model="data.item.compareAtPrice"
            type="number"
            min="1"
            step="1"
            class="price-input"
            @input="
              (e) => {
                const v = Number(e?.target?.value);
                if (!Number.isNaN(v) && v <= 0) data.item.compareAtPrice = 1;
              }
            "
            @blur="
              () => {
                const v = Number(data.item.compareAtPrice);
                if (!Number.isNaN(v) && v <= 0) data.item.compareAtPrice = 1;
                editingIndex = null;
              }
            "
            @keyup.enter="editingIndex = null"
            autofocus
          ></b-form-input>
        </div>
        <template v-else>
          $&nbsp;{{ data.item.regularPrice }}
        </template>
      </template>

      <template #cell(promotionalImportStatus)="data">
        <i class="icon-success" v-if="data.item.statusDisplay === 'valid'" v-b-tooltip.hover :title="data.item.statusMessage"></i>
        <i class="icon-warning text-warning" v-else-if="data.item.statusDisplay === 'warning'" v-b-tooltip.hover :title="data.item.statusMessage"></i>
        <i class="icon-error text-danger" v-else v-b-tooltip.hover :title="data.item.statusMessage"></i>
      </template>

      <template #cell(everydayImportStatus)="data">
        <i class="icon-success" v-if="data.item.statusDisplay === 'valid'" v-b-tooltip.hover :title="data.item.statusMessage"></i>
        <i class="icon-warning text-warning" v-else-if="data.item.statusDisplay === 'warning'" v-b-tooltip.hover :title="data.item.statusMessage"></i>
        <i class="icon-error text-danger" v-else v-b-tooltip.hover :title="data.item.statusMessage"></i>
      </template>
    </b-table>
    <table-pagination
      class="mt-3"
      :fromLimit="fromLimit | addLeadingZero"
      :toLimit="toLimit | addLeadingZero"
      :totalRecords="totalRecords | addLeadingZero"
      :currentPage="currentPage"
      :totalPages="totalPages"
      @paginate="paginate"
      :countInfo="`${moduleSlug}.table_count-info`"
      :recordsTitle="recordsTitle"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import config from "../../config";
import { forEach, ceil } from "lodash";
import moment from "moment";
import TablePagination from "./TablePagination.vue";
import { detectSkuNotMatch as detectSkuNotMatchHelper } from "../shared/helper";

const { tableHeaders, exportCSVHeaders } = config;

export default {
  components: { TablePagination },
  name: "ProductPreview",
  props: {
    formName: {
      type: String,
      default: "everyday-pricing",
    },
    showProductIcon: {
      type: Boolean,
      default: true,
    },
    perPage: {
      type: [String, Array],
      default: "20",
    },
    exportData: {
      type: Boolean,
      default: true,
    },
    fileTitle: {
      type: String,
      default: "data",
    },
    recordsTitle: {
      type: String,
      default: "products",
    },
    moduleSlug: {
      type: String,
      default: "everyday-pricing",
    },
    mode: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      currentPage: 1,
      editingIndex: null,
    };
  },
  computed: {
    ...mapGetters(["filteredProducts", "selectedSite"]),
    ...mapGetters("pricingManager", [
      "pricingManagerId",
      "activePromotions",
      "history",
      "validFlyerImportData",
      "validFlatFlyerData",
      "validEverydayImportData",
      "validPromotionalImportData",
    ]),
    ...mapGetters("tagManager", {
      tagManagerId: "tagManagerId",
      tagManagerHistory: "history",
    }),
    fields() {
      return tableHeaders[this.formName];
    },
    csvFields() {
      return exportCSVHeaders[this.formName];
    },
    productdata() {
      if (this.formName == "active-promotion") {
        return this.activePromotions;
      } else if (this.formName == "pricing-history") {
        return this.history;
      } else if (this.formName == "flyer-pricing-import") {
        return this.validFlyerImportData;
      } else if (this.formName == "promotional-pricing-import") {
        return this.validPromotionalImportData;
      } else if (this.formName == "flat-flyer-pricing") {
        return this.validFlatFlyerData;
      } else if (this.formName == "tag-history") {
        return this.tagManagerHistory;
      } else if (this.formName == "everyday-pricing-import") {
        return this.validEverydayImportData;
      } else {
        return this.filteredProducts;
      }
    },
    items() {
      return this.productdata;
    },
    totalRecords() {
      return this.productdata.length;
    },
    totalPages() {
      return ceil(this.productdata.length / this.perPage);
    },
    fromLimit() {
      let response = this.getPaginationResultLimit(
        this.currentPage,
        this.perPage,
        this.totalRecords
      );
      return response.fromlimit;
    },
    toLimit() {
      let response = this.getPaginationResultLimit(
        this.currentPage,
        this.perPage,
        this.totalRecords
      );
      return response.tolimit;
    },
    csvHeader() {
      let header = {};
      forEach(this.csvFields, (field) => {
        header[field.key] = field.label;
      });
      return header;
    },
    controllerName() {
      let controllerName = "";
      switch (this.formName) {
        case "flyer-pricing":
          controllerName = "FlyerPricing.vue";
          break;
        case "promotional-pricing":
          controllerName = "PromotionalPricing.vue";
          break;
        case "tag-manager":
          controllerName = "TagManager.vue";
          break;
        case "flat-flyer-pricing":
          controllerName = "FlatFlyerPricing.vue";
          break;
        case "flyer-pricing-import":
          controllerName = "FlyerPricingImport.vue";
          break;
        case "promotional-pricing-import":
          controllerName = "PromotionalPricingImport.vue";
          break;
        case "filtered-everyday-pricing-import":
          controllerName = "EverydayPricingImport.vue";
          break;
        case "everyday-pricing-import":
          controllerName = "EverydayPricingImport.vue";
          break;
        default:
          controllerName = "EverydayPricing.vue";
          break;
      }
      return controllerName;
    },
    moduleName() {
      let moduleName = "";
      switch (this.formName) {
        case "flyer-pricing":
          moduleName = "Flyer Pricing";
          break;
        case "promotional-pricing":
          moduleName = "Promotional Pricing";
          break;
        case "tag-manager":
          moduleName = "Tag manager";
          break;
        case "flat-flyer-pricing":
          moduleName = "Flat Flyer Pricing";
          break;
        case "flyer-pricing-import":
          moduleName = "Flyer Pricing Import";
          break;
        case "promotional-pricing-import":
          moduleName = "Promotional Pricing Import";
          break;
        case "filtered-everyday-pricing-import":
          moduleName = "Everyday Pricing Import";
          break;
        case "everyday-pricing-import":
          moduleName = "Everyday Pricing Import";
          break;
        default:
          moduleName = "Everyday Pricing";
          break;
      }
      return moduleName;
    },
  },
  watch: {
    productdata() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions("pricingManager", {
      pricingExportPreviewData: "getExportPreviewData",
    }),
    ...mapMutations({
      generalSetItem: "setItem",
    }),
    ...mapActions("tagManager", {
      tagExportPreviewData: "getExportPreviewData",
    }),
    prepareCSVdata(headers, productdata) {
      let csvData = [];
      forEach(productdata, (data) => {
        let _csvData = {};
        forEach(headers, (val, key) => {
          _csvData[key] = "";

          if (key == "pricingManagerId") {
            _csvData[key] = String(this.pricingManagerId).replace(/,/g, "");
          }

          if (key == "tagManagerId") {
            _csvData[key] = String(this.tagManagerId).replace(/,/g, ""); 
          }

          if (key == "pricing") {
            if ("pricing" in data) {
              let formList = [
                "everyday-pricing",
                "promotional-pricing",
                "active-promotion",
                "flyer-pricing",
                "flat-flyer-pricing",
              ];
              if (formList.includes(this.formName)) {
                _csvData[key] = String(data.pricing).replace(/,/g, "");
              }
            }
          }

          if (key == "productId") {
            if ("productId" in data) {
              _csvData[key] = String(data.productId).replace(/,/g, ""); 
            }
          }

          if (key == "products") {
            _csvData[key] = String(data.productName).replace(/,/g, ""); 
          }

          if (key == "vmn" || key == "vendorModelNumber") {
            if ("vendorModelNumber" in data) {
              _csvData[key] = String(data.vendorModelNumber).replace(/,/g, ""); 
            }
          }

          if (key == "sku") {
            if ("productSKU" in data) {
              _csvData[key] = String(data.productSKU).replace(/,/g, ""); 
            }
            if ("productId" in data) {
              _csvData[key] = String(data.productId).replace(/,/g, "");
            }
          }

          if (key == "regularPrice") {
            if ("regularPrice" in data) {
              _csvData[key] = String(data.regularPrice).replace(/,/g, ""); 
            }
          }
          if (key == "promoPrice") {
            if ("promoPrice" in data) {
              _csvData[key] = String(data.promoPrice).replace(/,/g, ""); 
            }
          }
          if (key == "everydayCost") {
            if ("replacementCost" in data) {
              _csvData[key] = String(data.replacementCost).replace(/,/g, ""); 
            }
          }
          if (key == "replacementCost") {
            if ("replacementCost" in data) {
              _csvData[key] = String(data.replacementCost).replace(/,/g, ""); 
            }
          }
          if (key == "vendor") {
            if ("vendor" in data) {
              _csvData[key] = String(data.vendor).replace(/,/g, ""); 
            }
          }
          if (key == "category") {
            if ("category" in data) {
              _csvData[key] = String(data.category).replace(/,/g, "");
            }
          }
          if (key == "productTags") {
            if ("productTag" in data) {
              _csvData[key] = String(data.productTag).replace(/,/g, ""); 
            }
          }
          if (key == "siteId") {
            if ("siteId" in data) {
              _csvData[key] = String(data.siteId).replace(/,/g, ""); 
            } else if (this.formName == "filtered-everyday-pricing-import") {
              _csvData[key] = String(this.selectedSite.SiteId).replace(
                /,/g,
                ""
              );
            }
          }
          if (key == "siteName") {
            if ("siteName" in data) {
              _csvData[key] = String(data.siteName).replace(/,/g, ""); 
            }
          }
          if (key == "flyerPrice") {
            if ("flyerPrice" in data) {
              _csvData[key] = String(data.flyerPrice).replace(/,/g, ""); 
            }
          }
          if (key == "compareAtPrice") {
            if ("compareAtPrice" in data) {
              _csvData[key] = String(data.compareAtPrice).replace(/,/g, ""); 
            } else if (this.formName == "filtered-everyday-pricing-import") {
              _csvData[key] = String(data.replacementCost).replace(/,/g, ""); 
            }
          }
          if (key == "cost") {
            if ("cost" in data && data.cost != null) {
              _csvData[key] = String(data.cost).replace(/,/g, "");
            } else if ("Cost" in data && data.Cost != null) {
              _csvData[key] = String(data.Cost).replace(/,/g, "");
            }
          }
          if (key == "flyerImportStatus") {
            if ("status" in data) {
              _csvData[key] =
                data.status == 1
                  ? "Success"
                  : data.statusMessage != null
                  ? String(data.statusMessage).replace(/,/g, "")
                  : "Failed";
            }
          }
        });
        csvData.push(_csvData);
      });
      return csvData;
    },
    async download() {
      try {
        this.generalSetItem({ resource: "isLoading", payload: true });
        let headers = this.csvHeader;
        let productdata = this.productdata;
        if (
          this.formName == "everyday-pricing" ||
          this.formName == "promotional-pricing" ||
          this.formName == "flyer-pricing" ||
          this.formName == "flat-flyer-pricing" ||
          this.formName == "everyday-pricing-import-filter"
        ) {
          productdata = await this.pricingExportPreviewData({
            pricingManagerId: this.pricingManagerId,
            exceptionParams: {
              userId: this.loggedUserId,
              controllerName: this.controllerName,
              moduleName: this.moduleName,
            },
          });
        } else if (this.formName == "tag-manager") {
          productdata = await this.tagExportPreviewData({
            productTagManagerId: this.tagManagerId,
            exceptionParams: {
              userId: this.loggedUserId,
              controllerName: this.controllerName,
              moduleName: this.moduleName,
            },
          });
        }

        let csvData = this.prepareCSVdata(headers, productdata);

        let fileTitle =
          this.fileTitle + "_" + moment(new Date()).format("YYYY/MM/DD");

        this.generalSetItem({ resource: "isLoading", payload: false });

        this.exportCSVFile(headers, csvData, fileTitle); // call the exportCSVFile() function to process the JSON and trigger the download
      } catch {
        this.generalSetItem({ resource: "isLoading", payload: true });
      }
    },
    disableEntry(id) {
      this.$emit("disableEntry", id);
    },
    async performEditClone(params) {
      this.$emit("performEditClone", params);
    },
    paginate(eventType) {
      if (eventType == "next") {
        this.currentPage = this.currentPage + 1;
      } else {
        this.currentPage = this.currentPage - 1;
      }
    },
    rowClass(item, type) {
      let rowClass = "text-nowrap";

      if (!item || type !== "row") return;
      if (item.statusDisplay === "error") return `${rowClass} tr-error`;
      if (item.statusDisplay === "warning") return `${rowClass} tr-warning`;
      if (item && type === "row" && item.status === 1) return rowClass;
      if (item && type === "row" && item.status === 0)
        return `${rowClass} bg-brand-alert-error`;
    },
    getSiteNames(item) {
      if (item.siteNames && Array.isArray(item.siteNames)) {
        return item.siteNames;
      }
      if (item.SiteName) {
        return [item.SiteName];
      }
      if (item.siteName) {
        return item.siteName.split(",");
      }
      return [];
    },
    isSkuNotInSite(item) {
      if (!item) return false;
      if (item.skuNotInSite) return true;
      return (
        item.statusMessages &&
        item.statusMessages.some((msg) => msg.includes("sku-not-in-site"))
      );
    },
    isSkuNotMatch(item) {
      return detectSkuNotMatchHelper(item);
    },
    shouldHideImportProductName(item) {
      if (!this.isImportValidationForm()) return false;
      return detectSkuNotMatchHelper(item);
    },
    isImportValidationForm() {
      return [
        "everyday-pricing-import",
        "flyer-pricing-import",
        "promotional-pricing-import",
      ].includes(this.formName);
    },
    canEditFlyerPromoImportPrice(item) {
      if (
        !["flyer-pricing-import", "promotional-pricing-import"].includes(
          this.formName
        )
      ) {
        return false;
      }

      // De-scoped: inline price editing disabled this iteration.
      return false;
    },
    shouldHideImportAmount(item) {
      if (!this.isImportValidationForm()) return false;
      return this.isSkuNotInSite(item) || this.isSkuNotMatch(item);
    },
    shouldHideImportVmn(item) {
      if (!this.isImportValidationForm()) return false;
      return this.isSkuNotMatch(item);
    },
    shouldHideImportCost(item) {
      if (!item) return true;
      if (this.shouldHideImportAmount(item)) return true;
      if (!this.isImportValidationForm()) return false;
      return (
        item.statusDisplay === "warning" || item.statusDisplay === "error"
      );
    },
    getEverydayImportCost(item) {
      if (!item) return "";
      return (
        item.cost ??
        item.replacementCost ??
        item.ReplacementCost ??
        item.Cost ??
        ""
      );
    },
    getImportCost(item) {
      if (!item) return "";
      return item.cost ?? item.Cost ?? "";
    },
  },
};
</script>

<style scoped>
.price-edit-container {
  width: 120px;
}
.price-text {
  padding: 0.375rem 0.75rem;
  height: calc(1.5em + 0.75rem + 2px);
  display: flex;
  align-items: center;
  border: 1px solid transparent;
  width: 100%;
}
.price-text.clickable:hover {
  cursor: pointer;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-color: #dee2e6;
}
.price-text.read-only {
  cursor: default;
}
.price-input {
  width: 100%;
}
.badge-orange-badge {
  background-color: #fbb040;
  color: #212121;
  padding: 3.008px 10px;
  font-weight: 500;
  border-radius: 20px;
  font-size: 11px;
}

::v-deep .tr-error {
  background-color: #fff5f5 !important;
}

::v-deep .tr-warning {
  background-color: #fff9e6 !important;
}

::v-deep .table td {
  vertical-align: middle;
}
</style>
